import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayView } from '@react-google-maps/api';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { openAreaTray } from '@actions/map-actions';
import { dotmapsClusterBlue, overlayTextColor } from '@constants/colors';
import {
  getMapModesSet,
  getTrayTop,
  getTrayHover,
  getTrayEntitiesIds,
  getTrayLayersIds,
  isAnyTrayItemHovered,
  getZoom
} from '@selectors/map-selector';
import { getHighestOverlapLevel } from '@utils/entity-utils';
import {
  isAnyTrayItemInCluster,
  isHoveredTrayEntityInCluster,
  isTrayEntityInCluster
} from '@utils/markers-utils';
import { shallowEqual } from '@utils/react-utils';
import './overlap-count-marker.scss';

const getEntities = entitiesContainer => {
  if (entitiesContainer) {
    const { data } = entitiesContainer;
    if (data) {
      const { entity, layer } = data;
      return { entity, layer };
    }
  }
  return null;
};

/**
 * Calculate if marker should be highlighted based on tray state.
 * @param {object} params - Parameters object.
 * @returns {boolean} Whether the marker should be highlighted.
 */
const shouldHighlight = ({
  items,
  isAnyHovered,
  traySingleEntries,
  trayEntitiesIds,
  trayLayersIds,
  trayOverlapHoverEntries
}) => {
  // Highlight the marker if:

  // There's a single entity in the tray, and it matches
  // one of the cluster entries:
  if (isTrayEntityInCluster(traySingleEntries, items)) {
    return true;
  }
  // Or if there are many entities in the tray list and
  // we are hovering one contained in the cluster:
  if (isHoveredTrayEntityInCluster(trayOverlapHoverEntries, items)) {
    return true;
  }
  // Or if we are not hovering any items on the tray,
  // and one of the elements in the tray is contained in the cluster:
  if (!isAnyHovered && isAnyTrayItemInCluster(trayEntitiesIds, trayLayersIds, items)) {
    return true;
  }
  return false;
};

/**
 * Custom comparison function for React.memo.
 * Returns true if props are equal (should NOT re-render).
 * Returns false if props are different (should re-render).
 * @param {object} prevProps - Previous props.
 * @param {object} nextProps - Next props.
 * @returns {boolean} Whether props are equal.
 */
const arePropsEqual = (prevProps, nextProps) => {
  // Re-render if center, modes, or items length changed
  if (!shallowEqual(nextProps.center, prevProps.center) ||
      nextProps.modes !== prevProps.modes ||
      Object.keys(nextProps.items).length !== Object.keys(prevProps.items).length) {
    return false; // props are NOT equal, should re-render
  }

  // Check if highlight status changed (only for exact clusters)
  if (!prevProps.isProximity) {
    // Check if any of the properties used to calculate the highlighting changed:
    if (!shallowEqual(nextProps.isAnyHovered, prevProps.isAnyHovered) ||
        !shallowEqual(nextProps.traySingleEntries, prevProps.traySingleEntries) ||
        !shallowEqual(nextProps.trayEntitiesIds, prevProps.trayEntitiesIds) ||
        !shallowEqual(nextProps.trayLayersIds, prevProps.trayLayersIds) ||
        !shallowEqual(nextProps.trayOverlapHoverEntries, prevProps.trayOverlapHoverEntries)) {
      const nextIsHighlighted = shouldHighlight(nextProps) || false;
      const prevIsHighlighted = shouldHighlight(prevProps) || false;
      // And finally check if the calculated highlight value changed:
      if (!shallowEqual(nextIsHighlighted, prevIsHighlighted)) {
        return false; // props are NOT equal, should re-render
      }
    }
  }

  // Check to see if entities or layers changed:
  let itemsChanged = false;
  Object.keys(prevProps.items).forEach(key => {
    if (!shallowEqual(nextProps.items[key], prevProps.items[key])) {
      itemsChanged = true;
    }
  });

  return !itemsChanged; // return true if items NOT changed (props are equal)
};

const OverlapCountMarker = ({
  center,
  count,
  isAnyHovered,
  isProximity,
  items,
  markerStyle = {},
  modes,
  traySingleEntries,
  trayEntitiesIds,
  trayLayersIds,
  trayOverlapHoverEntries,
  zoom
}) => {
  const dispatch = useDispatch();

  const isHighlighted = useMemo(() => {
    if (isProximity) {
      return false; // Proximity clusters can't be highlighted
    }
    return shouldHighlight({
      items,
      isAnyHovered,
      traySingleEntries,
      trayEntitiesIds,
      trayLayersIds,
      trayOverlapHoverEntries
    });
  }, [isAnyHovered, isProximity, items, trayEntitiesIds, trayLayersIds, trayOverlapHoverEntries, traySingleEntries]);

  const style = useMemo(() => {
    if (isProximity) {
      return '';
    }
    if (modes.size === 0 || isEmpty(items)) {
      return 'default';
    }
    // Flatten all entities into a single array:
    const entities = [].concat(...Object.values(items).map(item => Object.values(item)));
    return getHighestOverlapLevel(entities, modes) || 'default';
  }, [isProximity, items, modes]);

  const markerClick = useCallback(event => {
    // If the cluster contains items on the same location or
    // we are at the max zoom level, show the items on the tray:
    if (!isProximity || zoom >= 22) {
      dispatch(openAreaTray(center));
    } else {
      // If it's a proximity cluster, zoom in.
      //
      // I had to use this hack to access the Google Map's instance
      // and call the zoom method directly.
      // The proper way of doing this is by calling a Redux "Zoom"
      // action that will set the current zoom plus one on the store,
      // and then we render the map again.
      //
      // But since executing all that takes time, the user doesn't
      // have a good experience. This method (although a hack) is
      // as fast as when we click the "+" zoom icon on the map.
      window.mapInstance.setZoom(zoom + 1);
      // Also center the map to the marker center, when we zoom:
      window.mapInstance.setCenter(center);
    }
    event.stopPropagation();
    event.preventDefault();
  }, [center, dispatch, isProximity, zoom]);

  const getTransform = useCallback(highLighted => {
    if (highLighted) {
      return 'translateX(-13px) translateY(-33px)';
    }
    return 'translateX(-13px) translateY(-13px)';
  }, []);

  const getOuterClusterStyle = useCallback(highLighted => {
    if (highLighted) {
      // Add water drop style if highlighted:
      return {
        ...markerStyle,
        borderRadius: '50% 50% 50% 0'
      };
    }
    return markerStyle;
  }, [markerStyle]);

  // The inner div only needs the font and color set:
  const getInnerClusterStyle = useCallback((highLighted, styleArg) => {
    const padDiameter = diameter => {
      const newDiameter = parseFloat(diameter?.replace(/rem$/g, ''));
      return `${newDiameter - 0.25}rem`;
    };
    const innerStyle = {
      fontSize: markerStyle.fontSize,
      color: styleArg === 'default' ? markerStyle.color : overlayTextColor,
      height: padDiameter(markerStyle.height),
      width: padDiameter(markerStyle.width)
    };
    if (highLighted) {
      return {
        ...innerStyle,
        background: styleArg === 'default' ? dotmapsClusterBlue : '',
        color: overlayTextColor
      };
    }
    return innerStyle;
  }, [markerStyle]);

  return (
    <OverlayView
      data-testid="overlap-count-marker"
      mapPaneName={OverlayView.FLOAT_PANE}
      position={center}
      zIndex={150}
    >
      <div
        style={{
          zIndex: 150,
          position: 'absolute',
          msTransform: getTransform(isHighlighted),
          transform: getTransform(isHighlighted)
        }}
      >
        <div onClick={markerClick}
          styleName={`marker-overlap ${isProximity ? '' : 'exact-cluster'}`}
          style={getOuterClusterStyle(isHighlighted)}
          role="presentation">
          <div styleName={`marker-overlap-inner ${style} ${isProximity ? '' : 'exact-cluster'}`}
            style={getInnerClusterStyle(isHighlighted, style)}>
            {count}
          </div>
        </div>
        {/* Render it two times for exact type cluster, to show a shadow effect */}
        {!isProximity && (
          <div styleName="marker-overlap exact-cluster marker-overlap-stacked"
            style={getOuterClusterStyle(isHighlighted)}
            role="presentation">
            <div styleName={`marker-overlap-inner ${style} exact-cluster`}
              style={getInnerClusterStyle(isHighlighted, '')}>{ /* Style doesn't matter here,
                                                                   let's care only about inner
                                                                   circle diameter. */ }
              {count}
            </div>
          </div>
        )}
      </div>
    </OverlayView>
  );
};

OverlapCountMarker.propTypes = {
  center: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
  isAnyHovered: PropTypes.bool,
  isProximity: PropTypes.bool.isRequired,
  items: PropTypes.object.isRequired,
  markerStyle: PropTypes.object.isRequired,
  modes: PropTypes.instanceOf(Set),
  trayEntitiesIds: PropTypes.array,
  trayLayersIds: PropTypes.object,
  trayOverlapHoverEntries: PropTypes.object,
  traySingleEntries: PropTypes.object,
  zoom: PropTypes.number
};

// Wrap with memo and custom comparison function to replicate shouldComponentUpdate behavior
const MemoizedOverlapCountMarker = memo(OverlapCountMarker, arePropsEqual);

// Wrapper component that handles Redux connection
const OverlapCountMarkerContainer = props => {
  const zoom = useSelector(getZoom);
  const modes = useSelector(getMapModesSet);

  // Get highlight props only for non-proximity markers
  const trayEntitiesIds = useSelector(state => props.isProximity ? null : getTrayEntitiesIds(state));
  const trayLayersIds = useSelector(state => props.isProximity ? null : getTrayLayersIds(state));
  const traySingleEntries = useSelector(state => props.isProximity ? null : getEntities(getTrayTop(state)));
  const trayOverlapHoverEntries = useSelector(state => props.isProximity ? null : getEntities(getTrayHover(state)));
  const isAnyHovered = useSelector(state => props.isProximity ? null : isAnyTrayItemHovered(state));

  return (
    <MemoizedOverlapCountMarker
      {...props}
      zoom={zoom}
      modes={modes}
      trayEntitiesIds={trayEntitiesIds}
      trayLayersIds={trayLayersIds}
      traySingleEntries={traySingleEntries}
      trayOverlapHoverEntries={trayOverlapHoverEntries}
      isAnyHovered={isAnyHovered}
    />
  );
};

OverlapCountMarkerContainer.propTypes = {
  isProximity: PropTypes.bool.isRequired
};

export default OverlapCountMarkerContainer;
